import { cubicOut } from 'svelte/easing';

export function flyScale(
  node: Element,
  {
    delay = 0,
    duration = 400,
    easing = cubicOut,
    translate = {
      x: 0,
      y: 0
    } as { x?: string | number; y?: string | number },
    scale = {
      x: 1,
      y: 1
    } as { x?: number; y?: number },
    opacity = 0
  }
) {
  translate.x = translate.x ?? 0;
  translate.y = translate.y ?? 0;
  scale.x = scale.x ?? 0;
  scale.y = scale.y ?? 0;

  const unitX = `${translate.x}`.includes('%') ? '%' : 'px';
  const unitY = `${translate.y}`.includes('%') ? '%' : 'px';
  translate.x = parseFloat(`${translate.x}`);
  translate.y = parseFloat(`${translate.y}`);

  const style = getComputedStyle(node);
  const target_opacity = +style.opacity;
  const transform = style.transform === 'none' ? '' : style.transform;

  const od = target_opacity * (1 - opacity);

  return {
    delay,
    duration,
    easing,
    css: (t: number, u: number) => {
      return `
			transform: ${transform} translate(${(1 - t) * translate.x}${unitX}, ${(1 - t) * translate.y}${unitY}) scale(${
        scale.x + (1 - scale.x) * t
      }, ${scale.y + (1 - scale.y) * t});
			opacity: ${target_opacity - od * u}`;
    }
  };
}

interface ScaleFromOptions {
  delay: number;
  duration: number;
  easing: (t: number) => number;
  scaleTargetX: number;
  scaleTargetY: number;
  translateTargetX: number;
  translateTargetY: number;
}

export function scaleFrom(
  node: HTMLElement,
  {
    delay = 0,
    duration = 400,
    easing = cubicOut,
    scaleTargetX,
    scaleTargetY,
    translateTargetX,
    translateTargetY
  }: ScaleFromOptions
) {
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;

  let scaleX = 0,
    scaleY = 0;
  let translateX: number, translateY: number;
  if (transform === '') {
    const transform = getTransformForPixels(node, scaleTargetX, scaleTargetY, translateTargetX, translateTargetY);
    scaleX = transform.scaleX;
    scaleY = transform.scaleY;
    translateX = transform.translateX;
    translateY = transform.translateY;
  }

  const ssx = 1 - scaleX;
  const ssy = 1 - scaleY;

  function getTransformForPixels(
    node: HTMLElement,
    scaleTargetX: number,
    scaleTargetY: number,
    translateTargetX: number,
    translateTargetY: number
  ) {
    let scaleX = 1,
      scaleY = 1;
    let foundScaleX = false,
      foundScaleY = false;
    for (;;) {
      const { x, y, width, height } = node.getBoundingClientRect();

      if (!foundScaleX) {
        if (width - scaleTargetX < 0.1) {
          foundScaleX = true;
        } else {
          scaleX -= 0.001;
        }
      }
      if (!foundScaleY) {
        if (height - scaleTargetY < 0.1) {
          foundScaleY = true;
        } else {
          scaleY -= 0.001;
        }
      }

      if (foundScaleX && foundScaleY) {
        const res = { scaleX, scaleY, translateX: translateTargetX - x, translateY: translateTargetY - y };
        node.style.transform = '';
        return res;
      }

      node.style.transform = `scale(${scaleX}, ${scaleY})`;
    }
  }

  return {
    delay,
    duration,
    easing,
    css: (t: number, u: number) =>
      `transform: ${transform} translate(${(1 - t) * translateX}px, ${(1 - t) * translateY}px) scale(${1 - ssx * u}, ${
        1 - ssy * u
      });`
  };
}
